import { useUser } from '@clerk/clerk-react';
import { Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Pill, type PillTone } from '@/components/ui/pill';
import {
  useInvitePaymentHistory,
  useReverseEvent,
  type InvitePaymentEventRow,
  type InvitePaymentEventSource,
} from '@/hooks/useAdmin';
import { formatMoney } from '@/lib/money';
import { formatDateTime } from '@/lib/dates';
import { providerLabel } from '@/lib/labels';
import { actorLabel, paymentSourceLabel } from '@/lib/moneyPages';
import { useInvalidateMoney } from '@/hooks/useInvalidateMoney';
import { usePendingKeys } from '@/hooks/usePendingKeys';

/**
 * Full chronological payment history for one invite, in the invite's detail
 * sheet. Answers "why has this creator been paid $X?" in one lookup: every
 * payout, provider sync, adjustment, rejection write-off, import and undo,
 * with the paid total after each, and Undo where it's allowed.
 */
export function PaymentHistoryTab({
  invitePublicId,
}: {
  invitePublicId: string;
}): JSX.Element {
  const query = useInvitePaymentHistory(invitePublicId);
  const reverse = useReverseEvent();
  const invalidateMoney = useInvalidateMoney();
  // Per-entry in flight: the mutation hook only tracks its latest call.
  const pending = usePendingKeys();
  const { user } = useUser();
  const me = { id: user?.id ?? null, name: user?.fullName ?? null };

  if (query.isLoading) {
    return (
      <div className="text-xs text-muted-foreground">Loading payment history…</div>
    );
  }
  if (query.error) {
    return (
      <div className="text-xs text-destructive">
        Couldn't load payment history. {(query.error as Error).message}
      </div>
    );
  }
  const data = query.data;
  if (!data || data.events.length === 0) {
    return (
      <section className="mt-2 rounded-md border border-dashed p-4 text-sm">
        <h4 className="text-sm font-semibold">Payment history</h4>
        <p className="mt-1 text-xs text-muted-foreground">
          Nothing paid or adjusted yet. Earned so far: ${formatMoney(data?.invite.lockedValue)}.
        </p>
      </section>
    );
  }

  return (
    <section className="mt-2">
      <div className="mb-2 flex items-baseline justify-between">
        <h4 className="text-sm font-semibold">Payment history</h4>
        <div className="text-xs text-muted-foreground">
          {data.events.length} event{data.events.length === 1 ? '' : 's'}
        </div>
      </div>
      <div className="overflow-hidden rounded-md border">
        <table className="w-full text-xs">
          <thead className="bg-muted/40 text-[10px] uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-2 py-1.5 text-left">When</th>
              <th className="px-2 py-1.5 text-left">Source</th>
              <th className="px-2 py-1.5 text-right">Amount</th>
              <th className="px-2 py-1.5 text-right">Paid total after</th>
              <th className="px-2 py-1.5 text-left">Why · who · payout</th>
              <th className="px-2 py-1.5 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {data.events.map((e) => (
              <EventRow
                key={e.id}
                event={e}
                who={actorLabel(e.createdBy, me)}
                onReverse={(reason) =>
                  pending
                    .run(e.id, () => reverse.mutateAsync({ eventId: e.id, reason }))
                    .then((r) => {
                      if (!r) return;
                      invalidateMoney();
                      toast.success(
                        r.replayed
                          ? 'Already undone. Nothing changed.'
                          : `Undid the ${paymentSourceLabel(e.source).toLowerCase()} of $${formatMoney(e.signedAmount)}`,
                      );
                    })
                    .catch((err: Error) => {
                      toast.error(err.message);
                    })
                }
                pending={pending.has(e.id)}
              />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

const SOURCE_TONE: Record<InvitePaymentEventSource, PillTone> = {
  PAYOUT: 'success',
  RECONCILE: 'info',
  ADJUSTMENT: 'pending',
  REJECT: 'rejected',
  SETTLEMENT: 'submitted',
  IMPORT: 'muted',
  REVERSAL: 'muted',
};

// Imports and undos can't themselves be undone (see paymentLedger.reverse),
// and neither can anything already undone. Adjustments and write-offs are
// countered with a new adjustment from Adjust owed amount instead.
const REVERSIBLE_SOURCES = new Set<InvitePaymentEventSource>(['PAYOUT', 'RECONCILE']);

function EventRow({
  event,
  who,
  onReverse,
  pending,
}: {
  event: InvitePaymentEventRow;
  who: string;
  onReverse: (reason: string) => Promise<void>;
  pending: boolean;
}): JSX.Element {
  const signed = Number(event.signedAmount);
  const isPositive = signed > 0;
  const amountLabel = `${isPositive ? '+' : ''}$${formatMoney(event.signedAmount)}`;
  const reversed = event.reversedByEventId !== null;
  const canReverse =
    REVERSIBLE_SOURCES.has(event.source) && !reversed && event.reversesEventId === null;

  return (
    <tr className={reversed ? 'opacity-50' : ''}>
      <td className="whitespace-nowrap px-2 py-1.5 font-mono text-[10px] text-muted-foreground">
        {formatDateTime(event.createdAt)}
      </td>
      <td className="px-2 py-1.5">
        <Pill tone={SOURCE_TONE[event.source] ?? 'muted'}>{paymentSourceLabel(event.source)}</Pill>
      </td>
      <td
        className={`px-2 py-1.5 text-right font-mono tabular-nums ${
          isPositive ? 'text-success-strong' : 'text-destructive-strong'
        }`}
      >
        {reversed ? <span className="line-through">{amountLabel}</span> : amountLabel}
      </td>
      <td className="px-2 py-1.5 text-right font-mono tabular-nums text-muted-foreground">
        ${formatMoney(event.currentPaid)}
      </td>
      <td className="px-2 py-1.5">
        <div className="line-clamp-2">{event.reason}</div>
        <div className="mt-0.5 text-[10px] text-muted-foreground">
          by {who}
          {event.payout && (
            <>
              {' · '}
              {providerLabel(event.payout.providerKey)} payout
              {event.payout.paidAt ? `, paid ${formatDateTime(event.payout.paidAt)}` : ''}
            </>
          )}
          {event.reversesEventId !== null && <> · undoes an earlier entry</>}
          {reversed && <> · undone later</>}
        </div>
      </td>
      <td className="px-2 py-1.5 text-right">
        {canReverse && (
          <ConfirmDialog
            title={`Undo this ${paymentSourceLabel(event.source).toLowerCase()} of ${amountLabel}?`}
            description={
              <>
                Adds an Undo entry of{' '}
                <span className="tabular-nums">{isPositive ? '-' : '+'}${formatMoney(Math.abs(signed))}</span>,
                so this invite's paid total moves back by that amount. This entry stays in the
                history, crossed out. {event.payout ? providerLabel(event.payout.providerKey) : 'The provider'}{' '}
                is <span className="font-medium">not</span> contacted; if the money already went
                out, settle that separately.
              </>
            }
            tone="destructive"
            confirmLabel={`Undo ${amountLabel}`}
            cancelLabel="Keep event"
            reasonRequired
            reasonLabel="Undo reason"
            reasonPlaceholder="e.g. Wrong amount, disputed with the creator. Kept in the history."
            pending={pending}
            onConfirm={(reason) => onReverse(reason)}
            trigger={
              <Button
                size="sm"
                variant="ghost"
                disabled={pending}
                title="Undo this entry"
                aria-label={`Undo ${paymentSourceLabel(event.source).toLowerCase()} of ${amountLabel}`}
              >
                <Undo2 className="h-3 w-3" />
              </Button>
            }
          />
        )}
      </td>
    </tr>
  );
}
